import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface EditableFieldOption {
  value: string;
  label: string;
  icon?: string;
  group?: string;
}

interface EditableFieldProps {
  type: 'text' | 'textarea' | 'select' | 'date' | 'number';
  value: string | number | null | undefined;
  onSave: (value: string) => Promise<void> | void;
  placeholder?: string;
  emptyText?: string;
  className?: string;
  inputClassName?: string;
  options?: EditableFieldOption[];
  min?: number;
  max?: number;
  suffix?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
}

const EditableField: React.FC<EditableFieldProps> = ({
  type,
  value,
  onSave,
  placeholder,
  emptyText,
  className = '',
  inputClassName = '',
  options = [],
  min,
  max,
  suffix,
  rows = 3,
  disabled = false,
}) => {
  const { t } = useTranslation('tasks');
  const resolvedEmptyText = emptyText || t('editable.empty');
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // Sync local value when prop changes (from external update)
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(String(value ?? ''));
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' && inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleClick = () => {
    if (!disabled && !isEditing) {
      setIsEditing(true);
      setError(null);
    }
  };

  const handleBlur = async () => {
    const originalValue = String(value ?? '');
    if (localValue !== originalValue) {
      setIsSaving(true);
      setError(null);
      try {
        await onSave(localValue);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('editable.saveFailed'));
        setLocalValue(originalValue);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setLocalValue(String(value ?? ''));
      setIsEditing(false);
      setError(null);
    }
    if (e.key === 'Enter' && type !== 'textarea') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  // Display mode
  if (!isEditing) {
    const displayValue = type === 'select'
      ? options.find(o => o.value === String(value))?.label ?? value
      : value;

    const selectedOption = type === 'select'
      ? options.find(o => o.value === String(value))
      : null;

    const isEmpty = value === null || value === undefined || value === '';

    return (
      <div
        onClick={handleClick}
        className={`
          group cursor-pointer rounded px-2 py-1 -mx-2 -my-1
          hover:bg-gray-100 hover:pl-5 transition-all relative
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${className}
        `}
      >
        {isEmpty ? (
          <span className="text-gray-400 italic">{resolvedEmptyText}</span>
        ) : (
          <span className="flex items-center gap-1">
            {selectedOption?.icon && (
              <span>{selectedOption.icon}</span>
            )}
            <span>{displayValue}</span>
            {suffix && <span className="text-gray-400">{suffix}</span>}
          </span>
        )}
        {isSaving && (
          <span className="ml-2 inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
        )}
        {error && (
          <span className="ml-2 text-red-500 text-xs">{error}</span>
        )}
        {!disabled && !isSaving && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-50 transition-opacity">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </span>
        )}
      </div>
    );
  }

  // Edit mode
  const inputBaseClass = `
    w-full px-2 py-1 border border-blue-500 rounded
    focus:outline-none focus:ring-2 focus:ring-blue-200
    bg-white text-gray-900
    ${inputClassName}
  `;

  switch (type) {
    case 'textarea':
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          className={`${inputBaseClass} resize-none`}
        />
      );

    case 'select': {
      const hasGroups = options.some(o => o.group);
      const renderOption = (opt: EditableFieldOption) => (
        <option key={opt.value} value={opt.value}>
          {opt.icon ? `${opt.icon} ${opt.label}` : opt.label}
        </option>
      );

      let selectContent: React.ReactNode;
      if (hasGroups) {
        const groups: { name: string; items: EditableFieldOption[] }[] = [];
        const ungrouped: EditableFieldOption[] = [];
        for (const opt of options) {
          if (opt.group) {
            let g = groups.find(g => g.name === opt.group);
            if (!g) { g = { name: opt.group, items: [] }; groups.push(g); }
            g.items.push(opt);
          } else {
            ungrouped.push(opt);
          }
        }
        selectContent = (
          <>
            {ungrouped.map(renderOption)}
            {groups.map(g => (
              <optgroup key={g.name} label={g.name}>
                {g.items.map(renderOption)}
              </optgroup>
            ))}
          </>
        );
      } else {
        selectContent = options.map(renderOption);
      }

      return (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          value={localValue}
          onChange={(e) => {
            setLocalValue(e.target.value);
            setTimeout(() => inputRef.current?.blur(), 0);
          }}
          onBlur={handleBlur}
          className={inputBaseClass}
        >
          {selectContent}
        </select>
      );
    }

    case 'date':
      return (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="date"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={inputBaseClass}
        />
      );

    case 'number':
      return (
        <div className="flex items-center gap-1">
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            min={min}
            max={max}
            placeholder={placeholder}
            className={inputBaseClass}
          />
          {suffix && <span className="text-gray-400 text-sm flex-shrink-0">{suffix}</span>}
        </div>
      );

    default: // text
      return (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={inputBaseClass}
        />
      );
  }
};

export default EditableField;
